import { layoutParents, layoutDescendants } from "@frabr/fabric-editor";
import { DEFAULT_TRANSITION_DURATION, type SegmentTransition, type TransitionKind, transitionFor } from "./segment_transition";
/**
 * Format de composition v3 : segments plats, overlays absolus, et `src` en **référence**.
 *
 * { version:3, segments: [{ type, src: { url, content_medium_id } }], overlays, audio }
 *
 * Un segment ne porte plus une url mais une référence à un ContentMedium. L'url y est écrite par le
 * serveur quand le média est produit, ce qui donne une règle unique — url absente = pas encore
 * produit — valable pour un plan IA comme pour n'importe quel média en attente.
 */

// ==================== Types ====================

export interface SegmentSrc {
  url?: string | null;
  content_medium_id?: number;
  fallback_url?: string | null;
  fallback_type?: string | null;
  error?: string | null;
}

export interface Segment {
  id?: string;
  type: string;
  src?: SegmentSrc | null;
  placeholder?: boolean;
  label?: string;
  speed?: number;
  duration?: number;
  sourceStart?: number;
  sourceEnd?: number;
  sourceDuration?: number;
  backgroundColor?: string;
  /** Vers la séquence suivante (lib/segment_transition) ; la dernière n'en porte pas. */
  transition?: SegmentTransition | null;
  /** Le mouvement d'une séquence image, joué par creatorstudio (AnimatedImage). */
  animation?: { preset: string; from?: number; to?: number; duration?: number; speed?: number };
  /** Stade template : un plan généré par l'IA depuis une photo du sujet (casting à l'instanciation). */
  aiSource?: { transform?: string; wants?: string[]; fromImage?: string | string[] };
  _trailing?: boolean;
  /** Stade template : la séquence s'expanse en N selon les médias du sujet (lib Expansion). */
  repeat?: { over: string; range: [number, number] };
  /** Stade template : le fond de la séquence lié à un média du sujet (`src.url` = media[i]). */
  bindings?: Record<string, { expr: string; scope: string; resolved?: boolean }>;
}

export const SEGMENT_SLOT_FIELD = "src.url";

/** Une carte de médias dynamiques posée sur une séquence : son fond devient un slot du sujet —
 *  l'exemple reste dans `src.url` pour l'aperçu, le binding dit ce que l'expansion y mettra.
 *  Une carte de calque dit `image.src` ; sur une séquence le champ est toujours `src.url`. */
export function bindSegmentSlot(segment: Segment, dynamic: { expr: string; scope: string }): Segment {
  segment.bindings = { ...(segment.bindings || {}), [SEGMENT_SLOT_FIELD]: { expr: dynamic.expr, scope: dynamic.scope } };
  return segment;
}

/** Un vrai média posé sur une séquence liée : le slot s'efface, le média fait foi. */
export function unbindSegmentSlot(segment: Segment): Segment {
  if (segment.bindings) {
    delete segment.bindings[SEGMENT_SLOT_FIELD];
    if (!Object.keys(segment.bindings).length) delete segment.bindings;
  }
  return segment;
}

export const segmentSlot = (segment: Segment): string | null => segment.bindings?.[SEGMENT_SLOT_FIELD]?.expr ?? null;

// ==================== Le mouvement d'une séquence image ====================
//
// Un préréglage, joué par creatorstudio (AnimatedImage) : un zoom lent (Ken Burns) ou un
// travelling latéral sur toute la séquence. Les valeurs sont celles des familles ERB.

export const SEGMENT_MOTIONS = ["kenBurns", "travellingLeft", "travellingRight"] as const;
export type SegmentMotion = (typeof SEGMENT_MOTIONS)[number];

const MOTION_PRESETS: Record<SegmentMotion, NonNullable<Segment["animation"]>> = {
  kenBurns: { preset: "kenBurns", from: 1, to: 1.12 },
  travellingLeft: { preset: "travellingLeft", from: 70, to: 30 },
  travellingRight: { preset: "travellingRight", from: 30, to: 70 },
};

export function setSegmentMotion(segment: Segment, motion: SegmentMotion | null): void {
  if (motion && MOTION_PRESETS[motion]) segment.animation = { ...MOTION_PRESETS[motion] };
  else delete segment.animation;
}

export const segmentMotion = (segment: Segment): SegmentMotion | null => {
  const preset = segment.animation?.preset as SegmentMotion | undefined;
  return preset && SEGMENT_MOTIONS.includes(preset) ? preset : null;
};

// ==================== Les transitions entre séquences ====================

/**
 * Pose la même transition sur toutes les jointures de la timeline, en écrasant les
 * précédentes : chaque séquence visible sauf la dernière la porte vers la suivante. La durée
 * est plafonnée à la plus courte des deux voisines, pour qu'une transition ne déborde jamais
 * d'une séquence ; une voisine sans durée ne reçoit rien. `none` les retire toutes.
 */
export function setAllTransitions(composition: Composition, kind: TransitionKind): void {
  const segments = (composition.segments || []).filter((seg) => !seg._trailing);
  segments.forEach((seg, i) => {
    const next = segments[i + 1];
    const duration = next ? Math.min(DEFAULT_TRANSITION_DURATION, segmentOutputDuration(seg), segmentOutputDuration(next)) : 0;
    const transition = next ? transitionFor(kind, duration) : null;
    if (transition) seg.transition = transition;
    else delete seg.transition;
  });
}

interface SnapAnchor {
  segId: string;
  edge?: "start" | "end";
  offset?: number;
}

interface Animation {
  fromEnd?: boolean;
  delay?: number;
  duration?: number;
  _fromEndDelay?: number;
  /** Un balayage (lineWipe) : la couleur de sa barre et son sens (ltr par défaut). */
  color?: string;
  direction?: "ltr" | "rtl";
}

export interface Overlay {
  start: number;
  end: number;
  content?: string;
  color?: string;
  animations?: Animation[];
  snap?: { start?: SnapAnchor; end?: SnapAnchor };
  _hidden?: boolean;
  _fabricId?: string;
  /** La copie sérialisée des calques fabric — ce que lisent Remotion et les previews. Un
   *  conteneur de layout porte aussi ceux de ses enfants (cf. _syncOverlayLayers). */
  _fabricLayers?: FabricLayer[];
}

/** Un calque fabric sérialisé, vu du document : seul son rattachement nous intéresse ici. */
export interface FabricLayer {
  layerId?: string;
  layout?: { child?: { parentId?: string } };
}

/** Un morceau tel que le document le garde : sa référence (source, id) et de quoi l'afficher. */
export interface TrackRef {
  id: string;
  title?: string;
  source?: string;
  url?: string;
  moods?: string[];
  genres?: string[];
  primary_category?: string;
}

export interface Audio {
  /** L'url jouable ; absente au stade template (des candidats, pas encore de morceau). */
  src?: string;
  name?: string;
  offset?: number;
  duration?: number | null;
  startFrom?: number;
  fadeIn?: number;
  fadeOut?: number;
  volume?: number;
  track?: TrackRef;
  /** Stade template : les morceaux parmi lesquels chaque vidéo générée en tire un (le
   *  serveur, CompositionDocument::Music). */
  candidates?: TrackRef[];
}

/** La référence d'un morceau de la toolbox (le hash Soundtrack), telle que le document la garde. */
export function trackReference(track: Record<string, unknown>): TrackRef {
  const { id, title, source, url, moods, genres, primary_category } = track as TrackRef;
  return { id, title, source, url, moods, genres, primary_category };
}

export const audioCandidates = (audio: Audio | null | undefined): TrackRef[] => audio?.candidates ?? [];

/** Les réglages d'une musique (découpe, fondus, volume) : ceux en place, sinon les défauts —
 *  ils survivent au changement de morceau comme au passage par des candidats. */
export function audioSettings(previous: Partial<Audio> = {}): Pick<Audio, "startFrom" | "fadeIn" | "fadeOut" | "volume" | "offset" | "duration"> {
  return {
    startFrom: previous.startFrom ?? 0,
    fadeIn: previous.fadeIn ?? 1,
    fadeOut: previous.fadeOut ?? 1.5,
    volume: previous.volume ?? 0.5,
    offset: previous.offset ?? 0,
    duration: previous.duration ?? null,
  };
}

export interface Composition {
  version?: number;
  segments: Segment[];
  overlays?: Overlay[];
  audio?: Audio | null;
  fps?: number;
  width?: number;
  height?: number;
}

type Handle = "L" | "R";

// ==================== Internal ====================

/**
 * L'id d'une séquence neuve. Il vit dans le document sauvegardé, et des cibles le désignent
 * (fond de séquence, uploads figés) : unique pour de bon, comme ceux des pages — un compteur
 * repartirait de zéro au rechargement, une horloge se répète dans la même milliseconde.
 */
export function segmentId(): string {
  return `seg-${crypto.randomUUID()}`;
}

// ==================== Identifiants de segment ====================

/** Le format par défaut d'une composition vidéo (celui que l'éditeur affiche). */
export const DEFAULT_WIDTH = 1080;
export const DEFAULT_HEIGHT = 1920;

/**
 * Pose le format quand le document n'en porte pas : chaque lecteur appliquait son propre
 * défaut (l'éditeur 1920 de haut, le player de creatorstudio 1350) — le player cadrait
 * alors un 4:5 dans un cadre 9:16, et les habillages descendaient en lecture. Explicite,
 * le format part avec la preview et l'autosave l'écrit dans le document.
 */
export function ensureDimensions(composition: Composition): Composition {
  composition.width ||= DEFAULT_WIDTH;
  composition.height ||= DEFAULT_HEIGHT;
  return composition;
}

export function ensureSegmentIds(composition: Composition): Composition {
  for (const seg of composition.segments || []) {
    if (!seg.id) seg.id = segmentId();
  }
  return composition;
}

// ==================== Snap helpers ====================

export function segmentEdgeTimeById(
  composition: Composition,
  segId: string,
  edge: string,
): number | null {
  let acc = 0;
  for (const seg of composition.segments || []) {
    const dur = segmentOutputDuration(seg);
    if (seg.id === segId) return edge === "end" ? acc + dur : acc;
    acc += dur;
  }
  return null;
}

export function resolveSnaps(composition: Composition): void {
  for (const overlay of composition.overlays || []) {
    if (overlay.snap) {
      const s = overlay.snap;
      if (s.start) {
        const base = segmentEdgeTimeById(composition, s.start.segId, s.start.edge || "start");
        if (base !== null) overlay.start = Math.round((base + (s.start.offset || 0)) * 100) / 100;
        else delete s.start;
      }
      if (s.end) {
        const base = segmentEdgeTimeById(composition, s.end.segId, s.end.edge || "start");
        if (base !== null) overlay.end = Math.round((base + (s.end.offset || 0)) * 100) / 100;
        else delete s.end;
      }
      if (!s.start && !s.end) delete overlay.snap;
    }

    if (overlay.animations && overlay.start != null && overlay.end != null) {
      const dur = overlay.end - overlay.start;
      for (const anim of overlay.animations) {
        if (anim.fromEnd) {
          if (anim._fromEndDelay == null) anim._fromEndDelay = anim.delay || 0;
          anim.delay = Math.max(0, dur - anim._fromEndDelay - (anim.duration || 0.5));
        }
      }
    }
  }

  boundNestedOverlays(composition);
}

// ==================== Overlays imbriqués (conteneurs de layout) ====================
//
// L'enfant d'un conteneur est un overlay comme un autre : sa fenêtre, ses effets, son
// masquage. Le rendu (creatorstudio, FabricTree) le compose DANS son parent : borné dans le
// temps par lui, transformé avec lui, ses propres effets par-dessus. Le document porte donc
// cette contrainte : la fenêtre d'un enfant tient dans celle de son parent, et il n'a pas
// de snap segment (son référentiel est le parent). Le rattachement se lit dans les calques
// (`layout.child.parentId`), via l'arbre de layout de fabric-editor.

/** Le conteneur DIRECT de chaque overlay enfant (`_fabricId` → `_fabricId`). */
export function overlayContainers(overlays: Overlay[]): Map<string, string> {
  return layoutParents(overlays.flatMap((o) => o._fabricLayers || []));
}

/** Les overlays, des racines vers les feuilles — un parent toujours avant ses enfants. */
function overlaysByDepth(overlays: Overlay[], containers: Map<string, string>): Overlay[] {
  const depth = (overlay: Overlay) => {
    let id = overlay._fabricId;
    let n = 0;
    const seen = new Set<string>();
    while (id && containers.has(id) && !seen.has(id)) {
      seen.add(id);
      id = containers.get(id);
      n += 1;
    }
    return n;
  };
  return [...overlays].sort((a, b) => depth(a) - depth(b));
}

/**
 * La contrainte des enfants, posée dans le document à chaque résolution : fenêtre bornée
 * par celle du parent (un enfant entièrement hors de son parent reprend la fenêtre de
 * celui-ci — il n'existe pas en dehors), pas de snap segment.
 */
function boundNestedOverlays(composition: Composition): void {
  const overlays = composition.overlays || [];
  const containers = overlayContainers(overlays);
  if (containers.size === 0) return;

  const byId = new Map(overlays.filter((o) => o._fabricId).map((o) => [o._fabricId!, o]));
  for (const child of overlaysByDepth(overlays, containers)) {
    const parent = child._fabricId ? byId.get(containers.get(child._fabricId) ?? "") : undefined;
    if (!parent || parent === child) continue;

    delete child.snap;
    const start = Math.max(child.start ?? parent.start, parent.start);
    const end = Math.min(child.end ?? parent.end, parent.end);
    if (end > start) {
      child.start = start;
      child.end = end;
    } else {
      child.start = parent.start;
      child.end = parent.end;
    }
  }
}

/** Les descendants d'un overlay (ses `_fabricId`), à toute profondeur. */
export function overlayDescendants(overlays: Overlay[], fabricId: string): Overlay[] {
  const ids = layoutDescendants(overlayContainers(overlays), [fabricId]);
  return overlays.filter((o) => o._fabricId && ids.has(o._fabricId));
}

/**
 * Déplace un overlay ET sa descendance du même delta — un conteneur emporte ses enfants,
 * comme un groupe. Les enfants seront rebornés à la résolution suivante.
 */
export function moveOverlayTree(
  composition: Composition,
  overlay: Overlay,
  newStart: number,
  totalDuration: number,
): void {
  const before = overlay.start;
  moveOverlay(overlay, newStart, totalDuration);
  const delta = overlay.start - before;
  if (!delta || !overlay._fabricId) return;

  for (const child of overlayDescendants(composition.overlays || [], overlay._fabricId)) {
    child.start = Math.round((child.start + delta) * 100) / 100;
    child.end = Math.round((child.end + delta) * 100) / 100;
  }
}

/** Ce que l'éditeur montre d'un overlay au-delà de sa fenêtre : imbriqué, et masqué si lui
 *  ou un ancêtre l'est (un conteneur masqué emporte ses enfants au rendu). */
export interface NestedState {
  nested: boolean;
  hidden: boolean;
}

export function nestedStates(overlays: Overlay[]): NestedState[] {
  const containers = overlayContainers(overlays);
  const byId = new Map(overlays.filter((o) => o._fabricId).map((o) => [o._fabricId!, o]));

  return overlays.map((overlay) => {
    let hidden = Boolean(overlay._hidden);
    let id = overlay._fabricId;
    const seen = new Set<string>();
    while (id && containers.has(id) && !seen.has(id)) {
      seen.add(id);
      id = containers.get(id);
      hidden ||= Boolean(byId.get(id!)?._hidden);
    }
    return { nested: Boolean(overlay._fabricId && containers.has(overlay._fabricId)), hidden };
  });
}

function transferEndSnapsOnCut(
  composition: Composition,
  originalSegId: string,
  newSegId: string,
): void {
  for (const overlay of composition.overlays || []) {
    if (!overlay.snap?.end) continue;
    if (overlay.snap.end.segId === originalSegId && overlay.snap.end.edge === "end") {
      overlay.snap.end.segId = newSegId;
    }
  }
}

// ==================== Segment helpers ====================

export const segmentUrl = (seg: Segment): string | null => seg.src?.url ?? null;

export const isMediumMissing = (seg: Segment): boolean => Boolean(seg.src) && !segmentUrl(seg);

export const fallbackUrl = (seg: Segment): string | null => seg.src?.fallback_url ?? null;
export const fallbackType = (seg: Segment): string | null => seg.src?.fallback_type ?? null;

export const segmentError = (seg: Segment): string | null => seg.src?.error ?? null;

export const isPlayableVideo = (seg: Segment): boolean =>
  seg.type === "video" && !seg.placeholder && !isMediumMissing(seg);

export const hasPlaceholderSegment = (composition: Composition): boolean =>
  (composition.segments || []).some((seg) => Boolean(seg.placeholder));

export function segmentOutputDuration(seg: Segment): number {
  if (isPlayableVideo(seg))
    return ((seg.sourceEnd ?? 0) - (seg.sourceStart ?? 0)) / (seg.speed || 1);
  return seg.duration || 0;
}

export function totalOutputDuration(composition: Composition): number {
  return (composition.segments || []).reduce((sum, seg) => sum + segmentOutputDuration(seg), 0);
}

/** Le temps de sortie où commence la séquence d'index donné (seconds). */
export function segmentOutputStart(composition: Composition, index: number): number {
  return (composition.segments || [])
    .slice(0, index)
    .reduce((sum, seg) => sum + segmentOutputDuration(seg), 0);
}

/** Returns the segment at a given time (seconds). */
export function segmentAt(composition: Composition, time: number): Segment | null {
  let cursor = 0;
  for (const seg of composition.segments || []) {
    const dur = segmentOutputDuration(seg);
    if (time < cursor + dur) return seg;
    cursor += dur;
  }
  return null;
}

export function ensureDurationCoversOverlays(composition: Composition): void {
  const overlays = composition.overlays || [];
  const segments = composition.segments || [];

  const realDuration = segments.reduce(
    (sum, seg) => sum + (seg._trailing ? 0 : segmentOutputDuration(seg)),
    0,
  );

  const maxOverlayEnd = overlays.length > 0 ? Math.max(...overlays.map((o) => o.end || 0)) : 0;

  const needed = Math.round(Math.max(0, maxOverlayEnd - realDuration) * 100) / 100;

  const trailingIdx = segments.findIndex((s) => s._trailing);

  if (needed <= 0) {
    if (trailingIdx !== -1) segments.splice(trailingIdx, 1);
  } else if (trailingIdx !== -1) {
    segments[trailingIdx].duration = needed;
  } else {
    segments.push({
      type: "color",
      backgroundColor: "#000000",
      duration: needed,
      _trailing: true,
    });
  }
}

// ==================== Segment operations ====================

export function cutSegment(composition: Composition, segIndex: number, sourceTime: number): void {
  const seg = composition.segments[segIndex];
  if (!seg || !isPlayableVideo(seg)) return;
  if (sourceTime <= (seg.sourceStart ?? 0) || sourceTime >= (seg.sourceEnd ?? 0)) return;

  const newSegId = segmentId();
  const newSeg: Segment = {
    ...seg,
    sourceStart: sourceTime,
    id: newSegId,
  };
  seg.sourceEnd = sourceTime;
  seg.transition = null;
  transferEndSnapsOnCut(composition, seg.id!, newSegId);
  composition.segments.splice(segIndex + 1, 0, newSeg);
  resolveSnaps(composition);
}

/**
 * Coupe en deux la séquence vidéo sous le temps de sortie `time` (la tête de lecture) : le
 * temps de sortie ramené au temps source, vitesse comprise. Rend l'index de la séquence
 * coupée, ou null si rien n'est coupable là (pas une vidéo lisible, ou sur un bord).
 */
export function cutAtTime(composition: Composition, time: number): number | null {
  const index = (composition.segments || []).findIndex((seg) => seg === segmentAt(composition, time));
  if (index === -1) return null;
  const seg = composition.segments[index];
  const before = composition.segments.length;
  const sourceTime = (seg.sourceStart ?? 0) + (time - segmentOutputStart(composition, index)) * (seg.speed || 1);
  cutSegment(composition, index, sourceTime);
  return composition.segments.length > before ? index : null;
}

export function removeSegment(composition: Composition, segIndex: number): boolean {
  if (composition.segments.length <= 1) return false;
  composition.segments.splice(segIndex, 1);
  resolveSnaps(composition);
  return true;
}

// ==================== Suppression d'overlays ====================

/**
 * Retire les overlays qui portent ces `_fabricId`, AVEC leurs enfants de layout : les
 * calques d'un enfant sont fusionnés dans le conteneur (_syncOverlayLayers), donc supprimer
 * le conteneur seul laisserait des overlays d'enfants que rien ne dessine plus.
 *
 * @returns les `_fabricId` réellement retirés (conteneurs et enfants confondus)
 */
export function removeOverlaysByFabricId(composition: Composition, fabricIds: string[]): string[] {
  const overlays = composition.overlays || [];
  const doomed = new Set(fabricIds.filter(Boolean));
  if (doomed.size === 0) return [];

  // Les descendants des overlays condamnés, à toute profondeur : un conteneur emporte ses
  // enfants (le rattachement se lit dans les calques, cf. overlayContainers).
  layoutDescendants(overlayContainers(overlays), doomed).forEach((id) => doomed.add(id));

  const removed = overlays.filter((o) => o._fabricId && doomed.has(o._fabricId));
  composition.overlays = overlays.filter((o) => !o._fabricId || !doomed.has(o._fabricId));
  resolveSnaps(composition);

  return removed.map((o) => o._fabricId!);
}

/**
 * Le filet : retire les overlays fabric dont l'objet n'existe plus sur le canvas. Rattrape
 * tout chemin de suppression non câblé, et nettoie au premier geste les documents déjà
 * corrompus (une overlay orpheline y survit à l'autosave, et `restoreOverlays` la
 * ressusciterait au rechargement).
 *
 * Seuls les overlays fabric sont concernés : un overlay sans `_fabricId` n'a jamais eu
 * d'objet canvas et reste légitime.
 *
 * @returns les `_fabricId` retirés
 */
export function pruneOrphanOverlays(composition: Composition, liveFabricIds: string[]): string[] {
  const live = new Set(liveFabricIds);
  const orphans = (composition.overlays || [])
    .filter((o) => o._fabricId && !live.has(o._fabricId))
    .map((o) => o._fabricId!);

  return orphans.length ? removeOverlaysByFabricId(composition, orphans) : [];
}

export function setSegmentSpeed(composition: Composition, segIndex: number, speed: number): void {
  const seg = composition.segments[segIndex];
  if (!seg || !isPlayableVideo(seg) || speed <= 0) return;
  seg.speed = speed;
  resolveSnaps(composition);
}

export function trimSegment(
  composition: Composition,
  segIndex: number,
  handle: Handle,
  delta: number,
): void {
  const seg = composition.segments[segIndex];
  if (!seg) return;
  const round = (v: number) => Math.round(v * 100) / 100;

  if (isPlayableVideo(seg)) {
    const speed = seg.speed || 1;
    const srcDelta = delta * speed;
    const maxEnd = seg.sourceDuration ?? Infinity;

    if (handle === "R") {
      let newEnd = (seg.sourceEnd ?? 0) + srcDelta;
      let overflow = 0;
      if (newEnd > maxEnd) {
        overflow = newEnd - maxEnd;
        newEnd = maxEnd;
      }
      if (newEnd < (seg.sourceStart ?? 0) + 0.1) newEnd = (seg.sourceStart ?? 0) + 0.1;
      seg.sourceEnd = round(newEnd);
      if (overflow > 0) {
        seg.sourceStart = round(Math.max(0, (seg.sourceStart ?? 0) - overflow));
      }
    } else {
      let newStart = (seg.sourceStart ?? 0) + srcDelta;
      let overflow = 0;
      if (newStart < 0) {
        overflow = -newStart;
        newStart = 0;
      }
      if (newStart > (seg.sourceEnd ?? 0) - 0.1) newStart = (seg.sourceEnd ?? 0) - 0.1;
      seg.sourceStart = round(newStart);
      if (overflow > 0) {
        seg.sourceEnd = round(Math.min(maxEnd, (seg.sourceEnd ?? 0) + overflow));
      }
    }
  } else {
    if (handle === "R") {
      seg.duration = Math.max(0.5, round((seg.duration || 0) + delta));
    } else {
      seg.duration = Math.max(0.5, round((seg.duration || 0) - delta));
    }
  }
  resolveSnaps(composition);
}

export function reorderSegments(
  composition: Composition,
  fromIndex: number,
  toIndex: number,
): void {
  const segs = composition.segments;
  const [moved] = segs.splice(fromIndex, 1);
  segs.splice(toIndex, 0, moved);
  resolveSnaps(composition);
}

export function duplicateSegment(composition: Composition, segIndex: number): void {
  const seg = composition.segments[segIndex];
  if (!seg) return;
  composition.segments.splice(segIndex + 1, 0, {
    ...seg,
    src: seg.src && { ...seg.src },
    id: segmentId(),
  });
  resolveSnaps(composition);
}

// ==================== Overlay operations ====================

export function moveOverlay(overlay: Overlay, newStart: number, maxTime: number): void {
  delete overlay.snap;
  const dur = overlay.end - overlay.start;
  overlay.start = Math.max(0, Math.min(maxTime - 0.3, Math.round(newStart * 10) / 10));
  overlay.end = Math.round((overlay.start + dur) * 10) / 10;
}

export function trimOverlay(overlay: Overlay, handle: Handle, newPos: number): void {
  if (overlay.snap) {
    delete overlay.snap[handle === "L" ? "start" : "end"];
    if (!overlay.snap.start && !overlay.snap.end) delete overlay.snap;
  }
  if (handle === "L") {
    overlay.start = Math.round(Math.max(0, Math.min(newPos, overlay.end - 0.3)) * 10) / 10;
  } else {
    overlay.end = Math.round(Math.max(overlay.start + 0.3, newPos) * 10) / 10;
  }
}

export function toggleOverlayVisibility(composition: Composition, overlayIndex: number): void {
  const overlay = (composition.overlays || [])[overlayIndex];
  if (!overlay) return;
  overlay._hidden = !overlay._hidden;
}

// ==================== Audio operations ====================

export function audioStart(audio: Audio): number {
  return audio?.offset || 0;
}

export function audioEnd(audio: Audio | null, totalDuration: number): number {
  if (!audio) return 0;
  return audio.duration != null ? audioStart(audio) + audio.duration : totalDuration;
}

export function moveAudio(audio: Audio, newStart: number, maxTime: number): void {
  const start = audioStart(audio);
  const dur = audio.duration != null ? audio.duration : maxTime - start;
  const ns = Math.max(0, Math.min(maxTime - 0.3, Math.round(newStart * 10) / 10));
  audio.offset = ns;
  audio.duration = Math.round(Math.min(dur, maxTime - ns) * 10) / 10;
}

export function trimAudio(audio: Audio, handle: Handle, newPos: number, maxTime: number): void {
  const start = audioStart(audio);
  const end = audioEnd(audio, maxTime);
  if (handle === "L") {
    const ns = Math.round(Math.max(0, Math.min(newPos, end - 0.3)) * 10) / 10;
    audio.offset = ns;
    audio.duration = Math.round((end - ns) * 10) / 10;
  } else {
    const ne = Math.round(Math.max(start + 0.3, Math.min(newPos, maxTime)) * 10) / 10;
    audio.offset = start;
    audio.duration = Math.round((ne - start) * 10) / 10;
  }
}

// ==================== Utilities ====================

export function thumbnailUrl(src: string | null, sourceTime = 1, type = "video"): string | null {
  if (!src) return null;

  if (type === "image") {
    const match = src.match(/(.*\/image\/upload\/)(v\d+\/.*)/);
    if (!match) return src;
    return `${match[1]}f_jpg,w_300,c_fill/${match[2].replace(/\.\w+$/, ".jpg")}`;
  }

  const match = src.match(/(.*\/video\/upload\/)(v\d+\/.*)/);
  if (!match) return null;
  const time = Math.max(0, Math.round(sourceTime));
  return `${match[1]}so_${time},f_jpg,w_300,c_fill/${match[2].replace(/\.\w+$/, ".jpg")}`;
}

export function formatTime(t: number): string {
  return `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, "0")}`;
}

export function formatTimeShort(t: number): string {
  return `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}`;
}

export function segmentLabel(seg: Segment, index: number): string {
  if (seg.label) return seg.label;
  if (seg.placeholder) return "Vidéo (à ajouter)";
  if (seg.type === "color") return `Slide ${index + 1}`;
  if (seg.type === "image") return `Image ${index + 1}`;
  return `Segment ${index + 1}`;
}
